/**
 * Lines over time.
 *
 * ⚠ A port of `CS.charts.lines` from `mockups/charts.js` (screen 12, 26 Oct 2026), the same
 * way `GroupedBars` ports `CS.charts.groupedBars`: hand-rolled SVG, no charting library,
 * nothing animates.
 *
 * For a weekly history that grows by one point a week: a year of weeks as grouped bars
 * would be two pixels wide. Every point carries a hover title; dots are drawn only while
 * there is room for them, and the x labels are thinned to what fits, always keeping the
 * most recent one.
 *
 * ⚠ A value that is null or absent is NO DATA, not zero: the line breaks there and the
 * week keeps its place on the axis. Hugues, 26 Oct 2026: *"if you do not have the data,
 * show the weeks empty"* — a week we hold nothing for must never read as a week in which
 * nothing happened.
 */

import { formatNumber } from "@cocon-stats/shared";

export interface LineRow {
  label: string;
  /** The hover text for the point; the label when absent. */
  title?: string;
  [key: string]: string | number | null | undefined;
}

export interface LineSeries {
  key: string;
  label: string;
  color: string;
}

function niceMax(value: number): number {
  if (value <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  let nice = Math.ceil(value / magnitude) * magnitude;
  if (nice / value > 1.6) nice = (Math.ceil((value / magnitude) * 2) / 2) * magnitude;
  return nice;
}

export function Lines({
  rows,
  series,
  width = 640,
  height = 220,
}: {
  rows: LineRow[];
  series: LineSeries[];
  /** The width to draw at — pass the container's, or the drawing is letterboxed. */
  width?: number;
  height?: number;
}) {
  const padL = 42;
  const padR = 14;
  const padB = 34;
  const padT = 14;
  const plotW = width - padL - padR;
  const plotH = height - padB - padT;
  const n = rows.length;

  const valueOf = (row: LineRow, key: string): number | null => {
    const v = row[key];
    return typeof v === "number" ? v : null;
  };

  let max = 0;
  for (const row of rows) for (const s of series) max = Math.max(max, valueOf(row, s.key) ?? 0);
  max = niceMax(max);

  const x = (i: number) => padL + (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const y = (v: number) => padT + plotH - (max ? (v / max) * plotH : 0);

  // x labels: as many as fit at ~56px each, counted back from the latest week.
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 56))));
  const dots = n <= 40;
  const ticks = 4;

  return (
    <>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: `${height}px` }} role="img">
        <g className="chart-grid">
          {Array.from({ length: ticks + 1 }, (_, i) => {
            const gy = padT + plotH - (plotH * i) / ticks;
            return (
              <g key={i}>
                <line x1={padL} y1={gy} x2={width - padR} y2={gy} />
                <text className="chart-axis" x={padL - 8} y={gy + 3} textAnchor="end">
                  {Math.round((max * i) / ticks)}
                </text>
              </g>
            );
          })}
        </g>

        {rows.map((row, i) =>
          (n - 1 - i) % every === 0 ? (
            // The last label ends on its point: centred, half of it fell off the right edge.
            <text key={`x-${i}`} className="chart-axis" x={x(i)} y={height - padB + 16} textAnchor={n > 1 && i === n - 1 ? "end" : "middle"}>
              {row.label}
            </text>
          ) : null,
        )}

        {series.map((s) => {
          // One polyline per unbroken run of weeks that hold data.
          const runs: string[][] = [[]];
          rows.forEach((row, i) => {
            const v = valueOf(row, s.key);
            if (v === null) {
              if (runs[runs.length - 1]!.length) runs.push([]);
              return;
            }
            runs[runs.length - 1]!.push(`${x(i)},${y(v)}`);
          });
          return (
            <g key={s.key}>
              {runs
                .filter((pts) => pts.length)
                .map((pts, ri) => (
                  <polyline
                    key={ri}
                    points={pts.join(" ")}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                ))}
              {rows.map((row, i) => {
                const v = valueOf(row, s.key);
                if (v === null) return null;
                // An invisible dot still carries the hover title when there are too many to draw.
                return (
                  <circle key={i} cx={x(i)} cy={y(v)} r={dots ? 3 : 4} fill={s.color} fillOpacity={dots ? 1 : 0}>
                    <title>{`${row.title ?? row.label} · ${s.label}: ${formatNumber(v)}`}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>

      <div className="mt-1 flex flex-wrap items-center gap-4">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-slate-500">
            <span style={{ width: 12, height: 3, borderRadius: 2, background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
}
